// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import entryTemplate from '../../../templates/orderTemplate.entries.hbs'; // eslint-disable-line

import Page from '../page';
import { EntryData, OrderTemplate } from '../../model/order-template';
import OrderTemplateToolbar from '../../components/orders/order-template-toolbar';
import QuantitySelector from '../../components/inputs/quantity-selector';
import QuickAddToOrderTemplate from '../../components/orders/quick-add-to-order-template';
import { getParameterByName, encodeHTML, updateLocation } from '../../helpers/utils';
import MessageHelper from '../../helpers/message-helper';
import NotificationHelper from '../../helpers/notification-helper';
import AsyncStock from '../../components/product/async-stock';
import AsyncPrices from '../../components/product/async-prices';

export default class OrderTemplateDetailPage extends Page {

  private readonly orderTemplateBulkAddUrl: string;
  private readonly toolbar: OrderTemplateToolbar;
  private readonly orderTemplate: OrderTemplate;

  private readonly name: JQuery;
  private readonly description: JQuery;
  private readonly orderTemplateDetailContainer: JQuery;
  private readonly entriesContainer: JQuery;
  private readonly removeHeaderTitle: JQuery;
  private readonly entryControlsContainer: JQuery;
  private readonly url: string;

  private readonly qtySelector: string = '.js-qty-selector-input';
  private readonly entrySelector: string = '.js-entry';
  private readonly removeEntrySelector: string = '.js-removeEntry';

  private initialEntriesData: EntryData[];
  private initialName: string;
  private initialDescription: string;
  private editModeActive = false;

  constructor(private body: JQuery) {
    super();

    this.editModeActive = (getParameterByName('editMode') === 'true');

    this.orderTemplateDetailContainer = body.find('.js-orderTemplateDetailContainer');
    this.orderTemplateBulkAddUrl = this.orderTemplateDetailContainer.data('quick-order-add-to-cart-url') as string;
    this.removeHeaderTitle = body.find('.js-removeHeaderTitle');
    this.entriesContainer = body.find('.js-entriesContainer');
    this.entryControlsContainer = body.find('.js-entryControlsContainer');
    this.name = body.find('.js-name');
    this.description = body.find('.js-description');
    this.url = body.find('.add_to_cart_form').first().attr('action') as string;

    this.initialEntriesData = this.loadEntriesFromDom();
    this.initialName = this.orderTemplateDetailContainer.data('initial-name') as string;
    this.initialDescription = this.orderTemplateDetailContainer.data('initial-description') as string;

    // Clone the entries, so we can revert to original state
    const initialEntriesDataClone = OrderTemplateDetailPage.cloneEntries(this.initialEntriesData);
    this.orderTemplate = new OrderTemplate(
      this.orderTemplateDetailContainer.data('order-template-code'),
      this.name.val() as string,
      this.description.val() as string,
      initialEntriesDataClone,
    );

    this.toolbar = new OrderTemplateToolbar(
      body.find('.js-orderTemplateDetailContainer'),
      this.orderTemplate,
      this.editModeActive,
      this.bulkAddToCart,
      this.renderEntries,
      this.saveEdit,
      this.cancelEdit,
      this.toggleEditMode,
    );

    this.entryControlsContainer.toggleClass('d-none', !this.editModeActive);
    this.bindTableControls();
    this.bindRemoveEntry();
    this.bindUpdateProductQuantity();
  }

  private bindRemoveEntry(): void {
    this.entriesContainer.on('click', this.removeEntrySelector, (e) => {
      const entryElement = $(e.currentTarget).closest(this.entrySelector);
      const index = entryElement.data('element-index') as number;
      this.orderTemplate.removeEntry(index);
      this.renderEntries();
    });
  }

  private bindUpdateProductQuantity(): void {
    this.entriesContainer.on('change', this.qtySelector, (e) => {
      const index = $(e.currentTarget).closest(this.entrySelector).data('element-index') as number;
      const value = $(e.currentTarget).val() as number;
      this.orderTemplate.setProductQuantity(index, value);
    });
  }

  private bindTableControls(): void {
    this.toolbar.setupDeleteEntries();
    QuickAddToOrderTemplate.initAll(
      this.body.find('.js-quick-add-form'),
      this.orderTemplate,
      this.renderEntries,
    );
  }

  private loadEntriesFromDom(): EntryData[] {
    const entries: EntryData[] = [];

    this.entriesContainer.find(this.entrySelector).each((i, entryEl) => {
      const entry = $(entryEl);
      const qtyInput = entry.find(this.qtySelector);
      const missingProductUrl = '/_ui/responsive/common/images/missing-product.png';

      entries.push({
        productCode: entry.data('code') as string,
        name: entry.data('product-name') as string,
        price: entry.data('price') as string,
        quantity: qtyInput.val() as number,
        roundingQuantity: qtyInput.data('rounding-quantity') as number,
        imageUrl: entry.data('image-url') as string || missingProductUrl,
        partNumber: entry.data('part-number') as string,
        sapNetPrice: entry.data('sap-net-price') as string,
        customerStock: entry.data('customer-stock') as string,
        productUrl: entry.data('product-url') as string,
        showAddToCartButton: entry.data('show-add-to-cart') as boolean,
        blocked: entry.data('blocked') as boolean,
      });
    });

    return entries;
  }

  private toggleEditMode = (): void => {
    this.editModeActive = !this.editModeActive;
    this.toolbar.setEditMode(this.editModeActive);

    this.updateUrl();
    this.renderEntries();
    AsyncStock.initAll();
    AsyncPrices.initAll();
    this.removeHeaderTitle.toggleClass('d-none', !this.editModeActive);
    this.entryControlsContainer.toggleClass('d-none', !this.editModeActive);
    window.ACC.product.bindToAddToCartForm();
  };

  private saveEdit = (): void => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.orderTemplate.save()
      .done((data) => {
        window.NotificationHelper.clearNotifications();
        window.NotificationHelper.showInfo(encodeHTML(data));
        this.initialEntriesData = OrderTemplateDetailPage.cloneEntries(this.orderTemplate.getEntries());
        this.initialName = this.name.val() as string;
        this.toggleEditMode();
      })
      .fail((err) => {
        window.NotificationHelper.clearNotifications();
        window.NotificationHelper.showError(JSON.parse(err.responseText));
      });
  };

  private cancelEdit = (): void => {
    this.orderTemplate.setEntries(OrderTemplateDetailPage.cloneEntries(this.initialEntriesData));
    this.name.val(this.initialName);
    this.description.val(this.initialDescription);
    this.toggleEditMode();
  };

  private renderEntries = (): void => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const html = entryTemplate({
      orderTemplate: this.orderTemplate, editModeActive: this.editModeActive, url: this.url, encodedContextPath: window.ACC.config.encodedContextPath,
    }) as string;
    this.entriesContainer.html(html);
    QuantitySelector.initAll();
  };

  private bulkAddToCart = (): JQuery.jqXHR => $.ajax({
    url: this.orderTemplateBulkAddUrl,
    type: 'POST',
    dataType: 'json',
    contentType: 'application/json',
    data: this.getJSONDataForAddToCart(),
    async: false,
    success: () => OrderTemplateDetailPage.showBulkAddToCartConfirmation(),
    error: () => OrderTemplateDetailPage.showBulkAddToCartError(),
  });

  private getJSONDataForAddToCart(): string {
    const cartEntries = this.orderTemplate.getEntries()
      .filter((entry) => (entry.quantity > 0))
      .map((entry) => ({ product: { code: entry.productCode }, quantity: entry.quantity }));
    return JSON.stringify({ cartEntries });
  }

  static showBulkAddToCartConfirmation(): void {
    const message = MessageHelper.getMessage('cart.page.productsAdded');

    if (typeof window.ACC.minicart.updateMiniCartDisplay === 'function') {
      window.ACC.minicart.updateMiniCartDisplay();
    }

    NotificationHelper.Instance.showConfirmation(message);
  }

  static showBulkAddToCartError(): void {
    const message = MessageHelper.getMessage('cart.add.product.invalid');

    if (typeof window.ACC.minicart.updateMiniCartDisplay === 'function') {
      window.ACC.minicart.updateMiniCartDisplay();
    }

    NotificationHelper.Instance.showError(message);
  }

  private updateUrl(): void {
    let url = window.location.href;

    if (this.editModeActive) {
      url = url.concat('?editMode=true');
    } else {
      url = url.replace('?editMode=true', '');
    }

    updateLocation(url);
  }

  static cloneEntries(entries: EntryData[]): EntryData[] {
    return JSON.parse(JSON.stringify(entries)) as EntryData[];
  }
}
